/**
 * Allowed HTML tags for sanitization. These tags are considered safe to render in
 * the browser and are preserved by:
 *   - the vector service's `filterAnswer()` (LLM response → Core)
 *   - the frontend/webclient DOMPurify allowlist (HTML → DOM)
 *
 * Add a new tag here ONLY if it is user-facing HTML formatting. Internal sentinels
 * the LLM might emit for Core extraction do NOT belong here - the codebase no longer
 * uses any such sentinel (hot-topic classification is done via a separate dedicated
 * LLM call, not via an inline tag in the main response).
 */
export const ALLOWED_HTML_TAGS = [
  'br',
  'a',
  'img',
  'li',
  'ul',
  'ol',
  'b',
  'p',
  'strong',
  'i',
  'italic',
  'span',
  'em',
  'hr',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  // * <details> and <summary> are native disclosure elements — safe because they have
  // * no scripting capability and DOMPurify's attribute allowlist blocks any injected
  // * event handlers. They provide collapsible sections without any JavaScript.
  'details',
  'summary',
] as const;

/**
 * Allowed HTML attributes for sanitization
 */
// * `class` is allowed so collapsible sections can use the `conversed-collapsible` class.
// * This means LLM-generated HTML could inject arbitrary class names that match existing
// * CSS rules — accepted trade-off since we control the stylesheet and DOMPurify blocks
// * all scripting and event handler attributes.
export const ALLOWED_HTML_ATTRIBUTES = ['href', 'target', 'class'] as const;

/**
 * Regex pattern used by the vector service to filter HTML tags from LLM responses.
 * Removes all tags except those in ALLOWED_HTML_TAGS. Derived from the list at
 * module load so the two cannot drift.
 */
export const HTML_TAG_FILTER_REGEX = new RegExp(
  `<(?!\\/?(?:${ALLOWED_HTML_TAGS.join('|')})\\b)[^>]*>`,
  'gi',
);
